import type { ComputedRef } from 'vue'
import type { SyncPoliciesPayload } from '../../api/payment-policy/sync-policies.hook'
import type { TutorCategoryType } from '@entities/tutor'

import { useListsStore } from '@shared/model'

import { usePaymentPoliciesStore } from './store'
import { useSyncPoliciesApi } from '../../api/payment-policy/sync-policies.hook'
import { usePaymentPoliciesApi } from '../../api/payment-policy/payment-policies.hook'

interface ConfirmPaymentPolicyChange {
  category: TutorCategoryType
  label: string
  oldName: string
  newName: string
}

interface ModalConfirmPaymentPolicyReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: ComputedRef<boolean>
  refChanges: ComputedRef<ConfirmPaymentPolicyChange[]>
  reasons: Record<string, string>
  refIsValid: ComputedRef<boolean>
  closeModal: () => void
  saveChanges: () => Promise<void>
}

export const useModalConfirmPaymentPolicy = (): ModalConfirmPaymentPolicyReturn => {
  const paymentPoliciesStore = usePaymentPoliciesStore()
  const listsStore = useListsStore()
  const listsTutorCategory = computed( () => listsStore.getLists?.tutor.category)
  const reasons = reactive<Record<string, string>>({})

  const refIsOpen = computed<boolean>(() => paymentPoliciesStore.isModalConfirmPaymentPolicy)
  const refIsLoading = computed<boolean>(() => paymentPoliciesStore.isLoading)

  const refChanges = computed<ConfirmPaymentPolicyChange[]>(() => {
    const tutorPolicies = paymentPoliciesStore.getTutorPolicies

    return paymentPoliciesStore.getEdits.map(edit => {
      const oldPolicy = tutorPolicies.find(policy => policy.category === edit.category)

      return {
        category: edit.category,
        label: listsTutorCategory?.value?.[edit.category] ?? edit.category,
        oldName: oldPolicy?.name ?? 'For newbie',
        newName: edit.id === null
          ? 'Not set'
          : (edit.name || `#${edit.id}`),
      }
    })
  })

  const refIsValid = computed<boolean>(() =>
    refChanges.value.every(change => (reasons[change.category] ?? '').trim().length > 0),
  )

  const closeModal = (): void => {
    paymentPoliciesStore.setModalConfirmPaymentPolicy(false)
    for (const key of Object.keys(reasons)) {
      reasons[key] = ''
    }
  }

  const saveChanges = async (): Promise<void> => {
    if (!refIsValid.value) return
    const tutorId = paymentPoliciesStore.getTutorId

    if (!tutorId) return

    const payload: SyncPoliciesPayload = {
      tutors: [{ id: tutorId }],
      tutor_payment_policies: paymentPoliciesStore.getEdits.map(edit => ({
        id: edit.id,
        category: edit.category,
        reason: (reasons[edit.category] ?? '').trim(),
      })),
    }

    const isSuccess = await useSyncPoliciesApi({ payload })
    if (!isSuccess) return

    paymentPoliciesStore.setEdits([])
    closeModal()

    void usePaymentPoliciesApi({ tutorId })
  }

  return {
    refIsOpen,
    refIsLoading,
    refChanges,
    reasons,
    refIsValid,
    closeModal,
    saveChanges,
  }
}
